import React from 'react';
import { Coins, Users, PiggyBank } from 'lucide-react';
import { formatMoney } from '../../utils/format';

/**
 * DividendSplitPanel — 22/07/2026 (retour user). Curseurs de répartition
 * du dividende voté : prime collaborateurs (boost satisfaction) et/ou
 * dividende personnel (patrimoine dirigeant). Remplace le forfait 30 %.
 */
const STEP = 500;

export default function DividendSplitPanel({ max, bonus, personal, onChange, driversCount, personalWealth }) {
  const total = bonus + personal;
  const setBonus = (v) => onChange({ bonus: Math.min(v, max - personal), personal });
  const setPersonal = (v) => onChange({ bonus, personal: Math.min(v, max - bonus) });
  const satBoost = bonus > 0 && driversCount > 0
    ? Math.min(15, Math.max(1, Math.round((bonus / driversCount) / 400)))
    : 0;

  return (
    <div className="mt-3 bg-emerald-900/10 border border-emerald-500/30 rounded-lg p-3 space-y-3" data-testid="dividend-split-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-emerald-300 flex items-center gap-1.5">
          <Coins className="w-3.5 h-3.5" /> Répartition du dividende
        </p>
        <p className="text-xs font-mono text-slate-300" data-testid="dividend-total">
          Total : <span className={total > 0 ? 'text-emerald-300 font-bold' : 'text-slate-500'}>{formatMoney(total)}</span>
          <span className="text-slate-500"> / {formatMoney(max)} max</span>
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-slate-300 flex items-center gap-1">
            <Users className="w-3 h-3 text-sky-300" /> Prime collaborateurs ({driversCount} chauffeur·s)
          </span>
          <span className="font-mono text-sky-300">
            {formatMoney(bonus)}{satBoost > 0 ? ` → +${satBoost} satisfaction` : ''}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={max}
          step={STEP}
          value={bonus}
          onChange={e => setBonus(Number(e.target.value))}
          className="w-full accent-sky-400"
          data-testid="dividend-bonus-slider"
        />
      </div>

      <div>
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-slate-300 flex items-center gap-1">
            <PiggyBank className="w-3 h-3 text-emerald-300" /> Dividende personnel (patrimoine dirigeant)
          </span>
          <span className="font-mono text-emerald-300">
            {formatMoney(personal)}
            <span className="text-slate-500"> · patrimoine : {formatMoney(personalWealth)} → {formatMoney(personalWealth + personal)}</span>
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={max}
          step={STEP}
          value={personal}
          onChange={e => setPersonal(Number(e.target.value))}
          className="w-full accent-emerald-400"
          data-testid="dividend-personal-slider"
        />
      </div>

      <p className="text-[10px] text-slate-500 italic">
        La partie personnelle rembourse d&apos;abord votre apport en compte courant, le reste
        alimente votre patrimoine. +5 réputation/notoriété si le total distribué atteint 10 % du résultat net (+2 sinon).
      </p>
    </div>
  );
}
